import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, ErroRequisicao, get, mensagemDeErro } from '../nucleo/api';
import { dataHora, nomePessoa } from '../nucleo/formato';
import { trocaDeEmpresaLigada, useSessao, veTodasAsEmpresas } from '../nucleo/sessao';
import { Botao, Cartao, Segmentado, Selo } from './base';
import { Estado } from './estados';
import { Selecao } from './formulario';
import { Modal } from './sobreposicao';
import type { SelecaoTabela } from './tabela';
import { useToast } from './toast';

/**
 * V1 verticais (ficha 2026-10-20 "Verticais", núcleo 0321). Vertical = empresa do grupo. O cadastro marcado aparece a
 * quem vê alguma vertical dele ("Todas" = a todos). O cartão mostra e muda a marcação e o histórico de entrada e saída;
 * o selo resume a marcação nas listas. Com uma empresa só no workspace, não há o que marcar e o cartão não aparece.
 */
export interface EmpresaCatalogo {
  id: string;
  razao_social: string;
  nome_fantasia: string | null;
  /** Lista de 04/11, item 3: a moeda da empresa (Malgo International = USD). */
  moeda?: string;
}
interface LinhaHistorico {
  empresa_id: string | null;
  empresa_nome: string | null;
  outra: boolean;
  entrou_em: string;
  entrou_por_nome: string | null;
  saiu_em: string | null;
  saiu_por_nome: string | null;
}
interface Marcacao {
  empresas: string[];
  outras: number;
  todas: boolean;
  historico: LinhaHistorico[];
}

export const nomeDaEmpresa = (e: Pick<EmpresaCatalogo, 'razao_social' | 'nome_fantasia'>) => e.nome_fantasia || e.razao_social;

/** Empresas que quem está na sessão vê (as opções da marcação), do catálogo do núcleo. */
export function useEmpresasDoCatalogo() {
  return useQuery({ queryKey: ['/nucleo/catalogos'], queryFn: () => get<{ empresas: EmpresaCatalogo[] }>('/nucleo/catalogos'), staleTime: 60_000 });
}

/**
 * Resumo da marcação nas listas. Lote crm5 (E8): a marcação vazia ou com todas as verticais do catálogo vira a tag "Todas",
 * com o nome de cada uma ao passar o mouse; senão, uma tag por vertical (o nome dela).
 */
export function SeloVerticais({ empresas, catalogo }: { empresas: readonly string[] | null | undefined; catalogo: readonly EmpresaCatalogo[] }) {
  const { t } = useTranslation();
  if (!empresas || catalogo.length < 2) return null;
  if (ehTodasAsVerticais(empresas, catalogo))
    return (
      <span title={catalogo.map(nomeDaEmpresa).join(', ')} data-verticais-todas="">
        <Selo cor="neutro">{t('verticais.todas')}</Selo>
      </span>
    );
  return (
    <span className="inline-flex max-w-full min-w-0 flex-wrap gap-1" data-verticais-tags="">
      {empresas.map((id) => {
        const e = catalogo.find((x) => x.id === id);
        return (
          <Selo key={id} cor="ciano" quebra>
            {e ? nomeDaEmpresa(e) : t('verticais.outra')}
          </Selo>
        );
      })}
    </span>
  );
}

/** Marcação vazia (Todas) ou com cada vertical do catálogo. */
export const ehTodasAsVerticais = (empresas: readonly string[], catalogo: readonly Pick<EmpresaCatalogo, 'id'>[]) => !empresas.length || catalogo.every((e) => empresas.includes(e.id));

/**
 * Cartão "Verticais" da ficha de um cadastro marcado (`entidade` = `modulo.entidade` da rota de verticais). Lote crm5 (E11):
 * a entrada e a saída de cada vertical vão para a linha do tempo (chatter) do próprio registro; a lista no cartão só fica onde
 * a tela não tem o chatter (`comHistorico`).
 */
export function CartaoVerticais({ entidade, registroId, podeEditar, comHistorico = false }: { entidade: string; registroId: string; podeEditar: boolean; comHistorico?: boolean }) {
  const { t } = useTranslation();
  const catalogo = useEmpresasDoCatalogo();
  const chave = `/verticais/${entidade}/${registroId}`;
  const marcacao = useQuery({ queryKey: [chave], queryFn: () => get<Marcacao>(chave) });
  if ((catalogo.data?.empresas.length ?? 0) < 2) return null;
  return (
    <Cartao titulo={t('verticais.titulo')}>
      <Estado consulta={marcacao}>{(m) => <EditorVerticais chave={chave} chaveChatter={['chatter', entidade, registroId]} marcacao={m} empresas={catalogo.data!.empresas} podeEditar={podeEditar} comHistorico={comHistorico} />}</Estado>
    </Cartao>
  );
}

/**
 * Ficha 2026-10-22: o que prende o cadastro à vertical que sairia (409 `verticais.amarradas`), por empresa, com o link da
 * tela onde a pessoa desamarra. Nada foi gravado.
 */
export interface Amarracao {
  empresa_id: string;
  empresa_nome: string | null;
  chave: string;
  quantidade: number;
  descricao: string;
  link: string;
}

/** As amarrações de um erro da API (`null` quando o erro é outro). */
export function amarracoesDoErro(e: unknown): Amarracao[] | null {
  if (!(e instanceof ErroRequisicao) || e.corpo.code !== 'verticais.amarradas') return null;
  const lista = e.metaDoErro()?.amarracoes;
  return Array.isArray(lista) ? (lista as Amarracao[]) : [];
}

/** O alerta das amarrações: a lista (empresa, o que está ativo e quantos) e o link de cada item, no padrão dos impedimentos. */
export function AlertaAmarracoes({ itens }: { itens: Amarracao[] }) {
  const { t, i18n } = useTranslation();
  const frase = (a: Amarracao) => (i18n.exists(`verticais.amarracoes.${a.chave}_other`) ? t(`verticais.amarracoes.${a.chave}`, { count: a.quantidade }) : a.descricao);
  return (
    <div role="alert" className="flex flex-col gap-2" data-amarracoes="">
      <p className="text-sm text-tinta-2">{t('verticais.amarradas')}</p>
      <ul data-impedimentos="" className="divide-y divide-borda rounded-lg border border-borda bg-sup-2 text-sm">
        {itens.map((a, i) => (
          <li key={`${a.empresa_id}-${a.chave}-${i}`} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2">
            <span className="min-w-0">
              <span className="font-medium text-tinta-1">{a.empresa_nome ?? t('verticais.outra')}</span>
              {' · '}
              {frase(a)}
            </span>
            <Link to={a.link} className="shrink-0 text-acento-forte hover:underline">
              {t('verticais.abrir')}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** As caixas de escolha (Todas e cada vertical), as mesmas no cartão e no formulário de criação. */
function EscolhaVerticais({
  empresas,
  todas,
  escolhidas,
  podeEditar,
  podeTodas,
  aoMudarTodas,
  aoAlternar,
}: {
  empresas: EmpresaCatalogo[];
  todas: boolean;
  escolhidas: ReadonlySet<string>;
  podeEditar: boolean;
  podeTodas: boolean;
  aoMudarTodas: (v: boolean) => void;
  aoAlternar: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <label className={`flex items-center gap-2 text-sm ${podeTodas ? 'cursor-pointer' : 'text-tinta-3'}`}>
        <input type="checkbox" checked={todas} disabled={!podeTodas} onChange={(ev) => aoMudarTodas(ev.target.checked)} />
        {t('verticais.todas')}
      </label>
      <ul className="flex flex-col gap-1.5">
        {empresas.map((e) => (
          <li key={e.id}>
            <label className={`flex items-center gap-2 text-sm ${podeEditar && !todas ? 'cursor-pointer' : 'text-tinta-3'}`}>
              <input type="checkbox" checked={todas || escolhidas.has(e.id)} disabled={!podeEditar || todas} onChange={() => aoAlternar(e.id)} />
              {nomeDaEmpresa(e)}
            </label>
          </li>
        ))}
      </ul>
    </>
  );
}

const alternarEm = (c: ReadonlySet<string>, id: string) => {
  const n = new Set(c);
  if (n.has(id)) n.delete(id);
  else n.add(id);
  return n;
};

function EditorVerticais({ chave, chaveChatter, marcacao, empresas, podeEditar, comHistorico }: { chave: string; chaveChatter: string[]; marcacao: Marcacao; empresas: EmpresaCatalogo[]; podeEditar: boolean; comHistorico: boolean }) {
  const { t } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const veTodas = veTodasAsEmpresas(s);
  const [todas, setTodas] = useState(marcacao.todas);
  const [escolhidas, setEscolhidas] = useState(new Set(marcacao.empresas));
  const [amarradas, setAmarradas] = useState<Amarracao[] | null>(null);
  useEffect(() => {
    setTodas(marcacao.todas);
    setEscolhidas(new Set(marcacao.empresas));
    setAmarradas(null);
  }, [marcacao]);
  const salvar = useMutation({
    mutationFn: () => api('PUT', chave, { empresas: [...escolhidas], todas }),
    onSuccess: async () => {
      setAmarradas(null);
      await qc.invalidateQueries({ queryKey: [chave] });
      // E11: a mudança aparece na linha do tempo do registro.
      void qc.invalidateQueries({ queryKey: chaveChatter });
      toast.sucesso(t('comum.salvo'));
    },
    onError: (e) => {
      const lista = amarracoesDoErro(e);
      setAmarradas(lista);
      if (!lista) toast.erro(mensagemDeErro(e));
    },
  });
  const mudou = todas !== marcacao.todas || (!todas && (escolhidas.size !== marcacao.empresas.length || marcacao.empresas.some((id) => !escolhidas.has(id))));
  // "Todas" só muda quem vê todas as empresas (a API recusa os outros).
  const podeTodas = podeEditar && veTodas;
  return (
    <div className="flex flex-col gap-3" data-verticais="">
      <p className="text-xs text-tinta-3">{t('verticais.ajuda')}</p>
      <EscolhaVerticais
        empresas={empresas}
        todas={todas}
        escolhidas={escolhidas}
        podeEditar={podeEditar}
        podeTodas={podeTodas}
        aoMudarTodas={(v) => {
          setTodas(v);
          setAmarradas(null);
        }}
        aoAlternar={(id) => {
          setEscolhidas((c) => alternarEm(c, id));
          setAmarradas(null);
        }}
      />
      {marcacao.outras > 0 && <p className="text-xs text-tinta-3">{t('verticais.outras', { count: marcacao.outras })}</p>}
      {amarradas && <AlertaAmarracoes itens={amarradas} />}
      {podeEditar && (
        <div>
          <Botao disabled={!mudou || (!todas && !escolhidas.size && !marcacao.outras)} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            {t('verticais.salvar')}
          </Botao>
        </div>
      )}
      {comHistorico && marcacao.historico.length > 0 && (
        <section>
          <h4 className="kicker mb-1">{t('verticais.historico')}</h4>
          <ul className="flex flex-col gap-1 text-xs text-tinta-2" data-verticais-historico="">
            {marcacao.historico.map((h, i) => (
              <li key={i}>
                <span className="font-medium text-tinta-1">{h.outra ? t('verticais.outra') : (h.empresa_nome ?? t('verticais.todas'))}</span>
                {' · '}
                {t('verticais.entrou', { quando: dataHora(h.entrou_em), quem: h.entrou_por_nome ? nomePessoa(h.entrou_por_nome) : t('verticais.sistema') })}
                {h.saiu_em && (
                  <>
                    {' · '}
                    {t('verticais.saiu', { quando: dataHora(h.saiu_em), quem: h.saiu_por_nome ? nomePessoa(h.saiu_por_nome) : t('verticais.sistema') })}
                  </>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

type ModoLote = 'acrescentar' | 'tirar' | 'definir';
const MODOS_LOTE: readonly ModoLote[] = ['acrescentar', 'tirar', 'definir'];

/**
 * Seleção de linhas para marcar verticais em lote (até 500, a regra da API). A seleção vale para o filtro em que foi feita
 * (`chaveFiltro`): trocar o filtro troca as linhas e limpa a seleção, como a baixa em lote do Contas.
 */
export function useSelecaoVerticais<T extends { id: string }>(chaveFiltro: string, rotulo: (linha: T) => string, ativa: boolean) {
  const { t } = useTranslation();
  const [bruta, setBruta] = useState<{ chave: string; ids: Set<string> }>(() => ({ chave: '', ids: new Set() }));
  const marcados = bruta.chave === chaveFiltro ? bruta.ids : new Set<string>();
  const mudar = (f: (s: Set<string>) => Set<string>) => setBruta((a) => ({ chave: chaveFiltro, ids: f(a.chave === chaveFiltro ? a.ids : new Set()) }));
  const selecao: SelecaoTabela<T> | undefined = ativa
    ? {
        marcados,
        rotulo,
        rotuloTodas: t('verticais.selecionarTodos'),
        aoAlternar: (l, v) =>
          mudar((s) => {
            const n = new Set(s);
            if (v) n.add(l.id);
            else n.delete(l.id);
            return n;
          }),
        aoAlternarTodas: (linhas, v) =>
          mudar((s) => {
            const n = new Set(s);
            for (const l of linhas) {
              if (v) n.add(l.id);
              else n.delete(l.id);
            }
            return n;
          }),
      }
    : undefined;
  return { selecao, ids: [...marcados], limpar: () => mudar(() => new Set()) };
}

/** Barra da seleção com "Marcar verticais" (janela com o modo, as verticais e "Todas"). */
export function BarraVerticaisLote({ entidade, ids, aoLimpar, chaveLista }: { entidade: string; ids: string[]; aoLimpar: () => void; chaveLista: string }) {
  const { t } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const catalogo = useEmpresasDoCatalogo();
  const [aberto, setAberto] = useState(false);
  const [modo, setModo] = useState<ModoLote>('acrescentar');
  const [todas, setTodas] = useState(false);
  const [escolhidas, setEscolhidas] = useState(new Set<string>());
  const [amarradas, setAmarradas] = useState<Amarracao[] | null>(null);
  const veTodas = veTodasAsEmpresas(s);
  const aplicar = useMutation({
    mutationFn: () =>
      api<{ alterados: number; iguais: number; ignorados: number }>('POST', '/verticais/lote', { entidade, ids, modo, empresas: [...escolhidas], todas: modo !== 'tirar' && todas }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: [chaveLista] });
      toast.sucesso(t('verticais.resultadoLote', r));
      setAmarradas(null);
      setAberto(false);
      aoLimpar();
    },
    onError: (e) => {
      const lista = amarracoesDoErro(e);
      setAmarradas(lista);
      if (!lista) toast.erro(mensagemDeErro(e));
    },
  });
  if (!ids.length || (catalogo.data?.empresas.length ?? 0) < 2) return null;
  const comTodas = modo !== 'tirar' && todas;
  return (
    <section aria-label={t('verticais.selecionados', { count: ids.length })} className="flex min-w-0 flex-wrap items-center gap-2 rounded-xl border border-borda bg-sup-1 p-3" data-barra-selecao>
      <p className="min-w-0 flex-1 basis-48 text-sm font-medium text-tinta-1">{t('verticais.selecionados', { count: ids.length })}</p>
      <Botao variante="fantasma" tamanho="sm" onClick={aoLimpar}>
        {t('verticais.limparSelecao')}
      </Botao>
      <Botao tamanho="sm" onClick={() => setAberto(true)}>
        {t('verticais.marcarLote')}
      </Botao>
      <Modal
        aberto={aberto}
        aoFechar={() => {
          setAberto(false);
          setAmarradas(null);
        }}
        titulo={t('verticais.marcarLote')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setAberto(false)}>
              {t('comum.cancelar')}
            </Botao>
            <Botao variante="primario" disabled={!comTodas && !escolhidas.size} carregando={aplicar.isPending} onClick={() => aplicar.mutate()}>
              {t('verticais.aplicar')}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Segmentado
            rotulo={t('verticais.modo')}
            valor={modo}
            opcoes={MODOS_LOTE.map((m) => ({ valor: m, rotulo: t(`verticais.modos.${m}`) }))}
            aoMudar={(m) => {
              setModo(m);
              setAmarradas(null);
            }}
          />
          {modo !== 'tirar' && (
            <label className={`flex items-center gap-2 text-sm ${veTodas ? 'cursor-pointer' : 'text-tinta-3'}`}>
              <input type="checkbox" checked={todas} disabled={!veTodas} onChange={(ev) => setTodas(ev.target.checked)} />
              {t('verticais.todas')}
            </label>
          )}
          <ul className="flex flex-col gap-1.5">
            {(catalogo.data?.empresas ?? []).map((e) => (
              <li key={e.id}>
                <label className={`flex items-center gap-2 text-sm ${comTodas ? 'text-tinta-3' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={comTodas || escolhidas.has(e.id)}
                    disabled={comTodas}
                    onChange={() => {
                      setEscolhidas((c) => alternarEm(c, e.id));
                      setAmarradas(null);
                    }}
                  />
                  {nomeDaEmpresa(e)}
                </label>
              </li>
            ))}
          </ul>
          {amarradas && <AlertaAmarracoes itens={amarradas} />}
        </div>
      </Modal>
    </section>
  );
}

/** Escolha do formulário de criação; `null` = não mexeu (o cadastro nasce no escopo de quem cria, como antes). */
export type ValorVerticais = { todas: boolean; empresas: string[] } | null;

/** O que vai no corpo do POST de criação: nada quando a pessoa não mexeu. */
export const verticaisDaCriacao = (v: ValorVerticais) => (v ? { verticais: v.todas ? { todas: true, empresas: [] } : { todas: false, empresas: v.empresas } } : {});

/** A escolha do formulário de criação, zerada (padrão) a cada vez que o formulário abre (`abertura` muda). */
export function useVerticaisDaCriacao(abertura: unknown): [ValorVerticais, (v: ValorVerticais) => void] {
  const [valor, setValor] = useState<ValorVerticais>(null);
  useEffect(() => setValor(null), [abertura]);
  return [valor, setValor];
}

/**
 * Lista de 04/11, item 3 (D2-A): a moeda que as verticais apontam: as escolhidas ou, sem escolha, a empresa em que a pessoa está
 * ("Todas" não aponta nenhuma). Várias empresas em moedas diferentes também não. A mesma regra do servidor (`empresasDaCriacao`).
 */
export function moedaDasVerticais(valor: ValorVerticais, atual: string | null | undefined, empresas: readonly Pick<EmpresaCatalogo, 'id' | 'moeda'>[]): string | null {
  const ids = valor ? (valor.todas ? [] : valor.empresas) : atual ? [atual] : [];
  const moedas = new Set(empresas.filter((e) => ids.includes(e.id)).flatMap((e) => (e.moeda ? [e.moeda] : [])));
  return moedas.size === 1 ? [...moedas][0]! : null;
}

/**
 * A moeda sugerida num formulário de criação, na ordem do servidor: a da vertical, a padrão do cliente (quem lê o Cadastros, com
 * `parceiroId`) e a do workspace.
 */
export function useMoedaSugerida(verticais: ValorVerticais, parceiroId?: string | null): string {
  const s = useSessao();
  const catalogo = useEmpresasDoCatalogo();
  const daVertical = moedaDasVerticais(verticais, s.empresas?.atual, catalogo.data?.empresas ?? []);
  const parceiro = useQuery({
    queryKey: ['/cadastros/parceiros', parceiroId, 'moeda-padrao'],
    queryFn: () => get<{ moeda_padrao?: string | null }>(`/cadastros/parceiros/${parceiroId}`),
    enabled: !!parceiroId && !daVertical && s.pode('cadastros.ver'),
    retry: false,
    staleTime: 30_000,
  });
  return daVertical ?? (parceiroId ? parceiro.data?.moeda_padrao : null) ?? s.workspace.moeda;
}

/**
 * O campo `moeda` de um `Formulario` de criação: acompanha a sugestão até a pessoa escolher outra. `acompanhar` e `observar` vão nas
 * props do formulário; `reiniciar`, no "Salvar e criar outra".
 */
export function useMoedaDoFormulario(sugerida: string) {
  const [tocada, setTocada] = useState(false);
  return {
    acompanhar: tocada ? undefined : { moeda: sugerida },
    observar: (v: Record<string, unknown>) => {
      if (typeof v.moeda === 'string' && v.moeda && v.moeda !== sugerida) setTocada(true);
    },
    reiniciar: () => setTocada(false),
  };
}

/**
 * Ficha 2026-10-22: campo "Verticais" no formulário de criação, com as mesmas caixas do cartão. Mostra o padrão de hoje (a
 * empresa em que a pessoa está; em "Todas as empresas", Todas para quem vê todas) e só manda a escolha quando ela muda.
 * Com uma empresa só no workspace, não aparece.
 */
export function CampoVerticais({ valor, aoMudar, desabilitado = false }: { valor: ValorVerticais; aoMudar: (v: ValorVerticais) => void; desabilitado?: boolean }) {
  const { t } = useTranslation();
  const s = useSessao();
  const catalogo = useEmpresasDoCatalogo();
  const empresas = catalogo.data?.empresas ?? [];
  if (empresas.length < 2) return null;
  const veTodas = veTodasAsEmpresas(s);
  const atual = s.empresas?.atual ?? null;
  const padrao: NonNullable<ValorVerticais> = atual ? { todas: false, empresas: [atual] } : veTodas ? { todas: true, empresas: [] } : { todas: false, empresas: empresas.map((e) => e.id) };
  const v = valor ?? padrao;
  const escolhidas = new Set(v.empresas);
  return (
    <div className="col-span-full flex min-w-0 flex-col gap-1.5" data-verticais-criacao="">
      <span className="text-sm font-medium text-tinta-2">{t('verticais.titulo')}</span>
      <EscolhaVerticais
        empresas={empresas}
        todas={v.todas}
        escolhidas={escolhidas}
        podeEditar={!desabilitado}
        podeTodas={!desabilitado && veTodas}
        aoMudarTodas={(todas) => aoMudar({ todas, empresas: todas ? [] : v.empresas.length ? v.empresas : padrao.empresas })}
        aoAlternar={(id) => aoMudar({ todas: false, empresas: [...alternarEm(escolhidas, id)] })}
      />
      {/* Val1 (28/10): sem troca de empresa não existe "a vertical em que você está": nasce em Todas (quem vê todas) ou nas verticais da pessoa. */}
      <p className="text-xs text-tinta-3" data-verticais-ajuda="">
        {t(trocaDeEmpresaLigada(s) ? 'verticais.campoAjuda' : veTodas ? 'verticais.campoAjudaTodas' : 'verticais.campoAjudaSuas')}
      </p>
    </div>
  );
}

/** Filtro "Vertical" das listas: as verticais que a pessoa vê (o cadastro de "Todas" aparece em qualquer uma). */
export function FiltroVertical({ valor, aoMudar }: { valor: string; aoMudar: (v: string) => void }) {
  const { t } = useTranslation();
  const catalogo = useEmpresasDoCatalogo();
  if ((catalogo.data?.empresas.length ?? 0) < 2) return null;
  return (
    <div className="w-full sm:w-56">
      <Selecao
        aria-label={t('verticais.filtro')}
        opcoes={(catalogo.data?.empresas ?? []).map((e) => ({ valor: e.id, rotulo: nomeDaEmpresa(e) }))}
        vazio={t('verticais.filtroTodas')}
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
      />
    </div>
  );
}
